import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Zap, Wrench, Phone } from "lucide-react";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const HomePage = () => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [productsRes, categoriesRes] = await Promise.all([
                    publicApi.getFeaturedProducts(),
                    publicApi.getCategories()
                ]);
                setFeaturedProducts(productsRes.data);
                setCategories(categoriesRes.data);
            } catch (err) {
                console.error("Failed to fetch home data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);
    
    return (
        <div className="noise-overlay">
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden">
                {/* Background Video */}
                <div className="absolute inset-0">
                    <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="absolute w-full h-full object-cover"
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/dryice-marketplace/hero/background.mp4`}
                        poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
                    <div className="absolute inset-0 bg-background/20" />
                </div>
                
                <div className="container mx-auto px-6 lg:px-12 relative z-10">
                    <div className="max-w-3xl">
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-4 animate-fade-in">
                            Professional Equipment Marketplace
                        </p>
                        <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-none text-foreground mb-6 animate-fade-in stagger-1">
                            INDUSTRIAL<br />
                            <span className="text-primary">DRY ICE</span><br />
                            BLASTING
                        </h1>
                        <p className="text-muted-foreground text-lg md:text-xl max-w-xl mb-8 animate-fade-in stagger-2">
                            Professional-grade equipment for demanding applications. Expert guidance from selection to support.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
                            <Link to="/products" data-testid="hero-explore-btn">
                                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8 py-6 text-lg">
                                    Explore Equipment
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                            <Link to="/guidance" data-testid="hero-guidance-btn">
                                <Button variant="outline" className="border-2 border-foreground text-foreground hover:bg-foreground hover:text-background rounded-none uppercase font-bold tracking-wider px-8 py-6 text-lg">
                                    Get Guidance
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
                
                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                    <div className="w-6 h-10 border-2 border-muted-foreground rounded-full flex justify-center">
                        <div className="w-1 h-3 bg-primary rounded-full mt-2" />
                    </div>
                </div>
            </section>
            
            {/* Value Props */}
            <section className="py-16 lg:py-24 bg-card border-y border-border">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="flex items-start gap-4" data-testid="value-prop-1">
                            <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Shield className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-heading font-bold text-lg mb-2">Brand Agnostic</h3>
                                <p className="text-muted-foreground text-sm">We recommend the right equipment for your application, not a specific manufacturer.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4" data-testid="value-prop-2">
                            <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Zap className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-heading font-bold text-lg mb-2">Expert Guidance</h3>
                                <p className="text-muted-foreground text-sm">Our specialists help you select the right system for your specific needs.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4" data-testid="value-prop-3">
                            <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Wrench className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-heading font-bold text-lg mb-2">Full Support</h3>
                                <p className="text-muted-foreground text-sm">From purchase to operation, we're your single point of contact for support.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Featured Products */}
            <section className="py-16 lg:py-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="flex items-end justify-between mb-12">
                        <div>
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Featured Systems</p>
                            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase">
                                Professional Equipment
                            </h2>
                        </div>
                        <Link to="/products" className="hidden md:flex items-center gap-2 text-primary hover:underline font-mono text-sm uppercase tracking-wider">
                            View All
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-card border border-border h-96 animate-pulse" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {featuredProducts.slice(0, 3).map((product, index) => (
                                <Card 
                                    key={product.id} 
                                    className="bg-card border border-border rounded-none group hover:border-primary/50 transition-colors overflow-hidden"
                                    data-testid={`featured-product-${index}`}
                                >
                                    <div className="aspect-[4/3] bg-secondary overflow-hidden">
                                        {product.images?.[0] ? (
                                            <img 
                                                src={resolveImageUrl(product.images[0])} 
                                                alt={product.display_title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <span className="text-muted-foreground font-mono text-sm">No Image</span>
                                            </div>
                                        )}
                                    </div>
                                    <CardContent className="p-6">
                                        <p className="font-mono text-xs text-muted-foreground mb-2">{product.internal_name}</p>
                                        <h3 className="font-heading font-bold text-xl mb-2">{product.display_title}</h3>
                                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{product.short_description}</p>
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-primary font-bold">{product.price_text}</span>
                                            <Link to={`/products/${product.id}`}>
                                                <Button variant="ghost" size="sm" className="rounded-none text-primary hover:bg-primary/10">
                                                    View Details
                                                    <ArrowRight className="ml-1 h-4 w-4" />
                                                </Button>
                                            </Link>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                    
                    <div className="mt-8 md:hidden text-center">
                        <Link to="/products">
                            <Button variant="outline" className="rounded-none uppercase font-bold tracking-wider">
                                View All Equipment
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
            
            {/* Categories */}
            <section className="py-16 lg:py-24 bg-card border-y border-border">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Browse By Application</p>
                        <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase">
                            Equipment Categories
                        </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {categories.map((category, index) => (
                            <Link 
                                key={category.id} 
                                to={`/products?category=${category.slug}`}
                                className="group"
                                data-testid={`category-${index}`}
                            >
                                <div className="bg-secondary border border-border p-6 h-full hover:border-primary/50 transition-colors tech-corners">
                                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                                        {category.name}
                                    </h3>
                                    <p className="text-muted-foreground text-sm mb-4">
                                        {category.description}
                                    </p>
                                    <span className="inline-flex items-center gap-1 text-primary font-mono text-xs uppercase tracking-wider">
                                        Explore
                                        <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* CTA Section */}
            <section className="py-16 lg:py-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Get Guidance Card */}
                        <div className="bg-card border border-border p-8 lg:p-12 tech-corners" data-testid="cta-guidance">
                            <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-6">
                                <Phone className="h-6 w-6 text-primary" />
                            </div>
                            <h3 className="font-heading font-bold text-2xl md:text-3xl mb-4">Get Guidance</h3>
                            <p className="text-muted-foreground mb-6">
                                Not sure which system fits your application? Talk to a Dry Ice Cleaning Specialist and get the right setup from day one.
                            </p>
                            <Link to="/guidance">
                                <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-none uppercase font-bold tracking-wider px-8 py-6">
                                    Talk to a Specialist
                                </Button>
                            </Link>
                        </div>
                        
                        {/* Request Quote Card */}
                        <div className="bg-primary p-8 lg:p-12 tech-corners" data-testid="cta-quote">
                            <div className="w-12 h-12 bg-background/10 flex items-center justify-center mb-6">
                                <ArrowRight className="h-6 w-6 text-primary-foreground" />
                            </div>
                            <h3 className="font-heading font-bold text-2xl md:text-3xl mb-4 text-primary-foreground">Request a Quote</h3>
                            <p className="text-primary-foreground/80 mb-6">
                                Ready to move forward? Request pricing and availability for any system in our catalog.
                            </p>
                            <Link to="/quote">
                                <Button className="bg-background text-foreground hover:bg-background/90 rounded-none uppercase font-bold tracking-wider px-8 py-6">
                                    Get Quote
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
